import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  CircularProgress,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import { AMPLITUDE_KEYS, FEEDBACK_MESSAGES } from "../../../storage/constants";
import API from "../../../axios/instances/API";
import styled from "styled-components";
import * as amplitude from "@amplitude/analytics-browser";
import { Plus } from "react-feather";

const Wrapper = styled(Paper)`
  padding: 30px;
  min-width: min(600px, 96vw);
  max-width: 600px;
  text-align: left;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid black;
`;

const CHARACTER_LIMIT = 58;

const CreateAudience = ({ setAddAudienceOpen, isRetail }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [audienceName, setAudienceName] = useState("");
  const [errorAlert, setErrorAlert] = useState("");
  const [charsMaxed, setCharsMaxed] = useState(false);

  const history = useHistory();

  const createAudience = async (e) => {
    e.preventDefault();
    setErrorAlert(false);
    setIsSubmitting(true);
    try {
      const { data } = await API.post("dashboard/audiences/", {
        name: audienceName,
      });
      amplitude.track(AMPLITUDE_KEYS.CREATED_AUDIENCE);
      setIsSubmitting(false);
      setAddAudienceOpen(false);
      history.push(`/audience/detail/${data.id}`);
    } catch (error) {
      setErrorAlert(true);
      setIsSubmitting(false);
    }
  };

  const handleUpdateAudienceName = (e) => {
    const { value } = e?.target;

    const numChars = value?.length || 0;

    if (numChars > CHARACTER_LIMIT) {
      return setCharsMaxed(true);
    }

    setCharsMaxed(false);
    setAudienceName(value);
  };

  const buttonDisabled = charsMaxed || audienceName.length < 3 || isSubmitting;

  return (
    <Wrapper>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <Typography variant="h3">
          {isRetail ? "Create Audience Template" : "Create Custom Audience"}
        </Typography>
      </div>
      {isRetail ? (
        <Typography
          variant="subtitle1"
          style={{ lineHeight: 1.4, marginTop: 4 }}
        >
          We’ll save this name as an easy reminder of which groups of employees
          and locations you’re communicating with when you choose this template
          . Be descriptive! For example, “All Managers, Northwest region” or
          “All locations, All roles except Managers".
        </Typography>
      ) : (
        <Typography
          variant="subtitle1"
          style={{ lineHeight: 1.4, marginTop: 4 }}
        >
          Fill out the field below to create a new Audience.
        </Typography>
      )}
      <form
        style={{ display: "flex", flexDirection: "column" }}
        id="createAudienceForm"
        onSubmit={createAudience}
      >
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          sx={{ mt: 10 }}
          autoFocus
          error={charsMaxed}
          helperText={
            charsMaxed &&
            `${CHARACTER_LIMIT} characters maximum for audience name`
          }
          label="New Audience Name"
          value={audienceName}
          onChange={handleUpdateAudienceName}
        />
        <Button
          type="submit"
          form="createAudienceForm"
          variant="contained"
          color="primary"
          disabled={buttonDisabled}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={20} />
            ) : (
              <Plus height={14} width={14} color="white" />
            )
          }
          sx={{ mt: 4, ml: "auto" }}
        >
          {isSubmitting ? "Creating Audience" : "Create Audience"}
        </Button>
      </form>
      {errorAlert && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          fullWidth
          onClose={() => setErrorAlert(false)}
        >
          {FEEDBACK_MESSAGES.GENERIC_ERROR}
        </Alert>
      )}
    </Wrapper>
  );
};

export default CreateAudience;
